"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { stegaClean } from "next-sanity";

import { cn } from "@/lib/utils";

import type {
  CommunityCountryKey,
  ICommunityWorldMapCountry,
} from "./types";
import WorldMapCountryCard from "./WorldMapCountryCard";

interface CommunityWorldMapProps {
  statusPrefix?: string;
  statusCountry?: string;
  dragLabel?: string;
  localTimeLabel?: string;
  onFloorLabel?: string;
  floorCapacity?: number;
  countries?: ICommunityWorldMapCountry[];
}

const CARD_POSITIONS: Record<
  CommunityCountryKey,
  { left: string; top: string }
> = {
  uk: { left: "50.58%", top: "24.89%" },
  germany: { left: "53.7%", top: "25.51%" },
  italy: { left: "53.36%", top: "30.49%" },
  spain: { left: "49.42%", top: "34.85%" },
  brazil: { left: "38.77%", top: "14.93%" },
  singapore: { left: "79.4%", top: "65.34%" },
  japan: { left: "88.77%", top: "38.89%" },
};

const COUNTRY_LAYERS = [
  {
    country: "uk",
    src: "/community/world-map/world-map-uk.svg",
    activeSrc: "/community/world-map/world-map-uk-active.svg",
    className: "top-[19.89%] left-[48.7%] h-[4.03%] w-[1.51%]",
  },
  {
    country: "germany",
    src: "/community/world-map/world-map-de.svg",
    activeSrc: "/community/world-map/world-map-de-active.svg",
    className: "top-[22.58%] left-[52.1%] h-[3.76%] w-[1.91%]",
  },
  {
    country: "italy",
    src: "/community/world-map/world-map-it.svg",
    activeSrc: "/community/world-map/world-map-it-active.svg",
    className: "top-[29.03%] left-[52.1%] h-[5.11%] w-[2.41%]",
  },
  {
    country: "spain",
    src: "/community/world-map/world-map-es.svg",
    activeSrc: "/community/world-map/world-map-es-active.svg",
    className: "top-[30.38%] left-[47.8%] h-[3.76%] w-[2.81%]",
  },
  {
    country: "brazil",
    src: "/community/world-map/world-map-br.svg",
    activeSrc: "/community/world-map/world-map-br-active.svg",
    className: "top-[59.95%] left-[30%] h-[27.15%] w-[10.11%]",
  },
  {
    country: "singapore",
    src: "/community/world-map/world-map-sg.svg",
    activeSrc: "/community/world-map/world-map-sg-active.svg",
    className: "top-[59.78%] left-[78.23%] h-[3.54%] w-[1.30%]",
  },
  {
    country: "japan",
    src: "/community/world-map/world-map-jp.svg",
    activeSrc: "/community/world-map/world-map-jp-active.svg",
    className: "top-[29.03%] left-[86.2%] h-[9.14%] w-[3.81%]",
  },
] as const;

const CITY_MARKERS = [
  {
    country: "uk",
    src: "/community/world-map/world-map-london.svg",
    left: "49.497%",
    top: "23.03%",
    label: "London",
  },
  {
    country: "germany",
    src: "/community/world-map/world-map-frankfurt.svg",
    left: "51.947%",
    top: "24.05%",
    label: "Frankfurt",
  },
  {
    country: "germany",
    src: "/community/world-map/world-map-frankfurt.svg",
    left: "53.257%",
    top: "22.25%",
    label: "Berlin",
  },
  {
    country: "italy",
    src: "/community/world-map/world-map-milan.svg",
    left: "52.087%",
    top: "27.52%",
    label: "Milan",
  },
  {
    country: "spain",
    src: "/community/world-map/world-map-madrid.svg",
    left: "48.507%",
    top: "31.31%",
    label: "Madrid",
  },
  {
    country: "brazil",
    src: "/community/world-map/world-map-sao-paulo.svg",
    left: "36.588%",
    top: "79.08%",
    label: "Sao Paulo",
  },
  {
    country: "singapore",
    src: "/community/world-map/world-map-singapore.svg",
    left: "78.377%",
    top: "60.48%",
    label: "Singapore",
  },
  {
    country: "japan",
    src: "/community/world-map/world-map-tokyo.svg",
    left: "88.327%",
    top: "34.83%",
    label: "Tokyo",
  },
] as const;

/**
 * The map opens with one city already selected. Without it the dots read as
 * decoration and the interaction goes unnoticed on both pointers — there is
 * nothing to hover on desktop and nothing to tap on mobile until you guess.
 * London is the opening city because the status line above the map names the
 * United Kingdom by default, so the sentence and the card agree on first paint.
 * The first hover, tap or drag hands control back to the visitor.
 *
 * It is selected after mount, not in the initial state: the card prints a live
 * local time and this page is statically generated, so a server-rendered card
 * would hydrate against whatever clock the build or the last revalidation saw.
 */
const OPENING_MARKER = CITY_MARKERS[0];
const OPENING_MARKER_KEY = `${OPENING_MARKER.country}-${OPENING_MARKER.label}`;

export default function CommunityWorldMap({
  statusPrefix,
  statusCountry,
  dragLabel,
  localTimeLabel,
  onFloorLabel,
  floorCapacity,
  countries,
}: CommunityWorldMapProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({
    active: false,
    startX: 0,
    startScrollLeft: 0,
  });
  const reduceMotion = useReducedMotion();
  const [activeCountryKey, setActiveCountryKey] =
    useState<CommunityCountryKey | null>(null);
  const [activeMarkerKey, setActiveMarkerKey] = useState<string | null>(null);
  const openingCardRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const trackFillRef = useRef<HTMLSpanElement>(null);
  const scrubbing = useRef(false);
  const openingCardFramed = useRef(false);
  // The opening selection is a hint, not a choice the visitor made, so the tap
  // toggle below must not read it as "already open" — otherwise the first tap
  // on the opening city spends itself closing a card nobody opened.
  const openingHint = useRef(false);
  const [now, setNow] = useState(() => new Date());
  const lastPointerType = useRef("mouse");

  const countriesByKey = useMemo(
    () =>
      new Map(
        (countries ?? []).map((country) => {
          const key = stegaClean(country.key) as CommunityCountryKey;
          return [
            key,
            {
              ...country,
              key,
              name: stegaClean(country.name),
              timeZone: stegaClean(country.timeZone),
            },
          ];
        }),
      ),
    [countries],
  );
  const activeCountry = activeCountryKey
    ? countriesByKey.get(activeCountryKey)
    : undefined;
  const activeCountryPosition = activeCountryKey
    ? CARD_POSITIONS[activeCountryKey]
    : undefined;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || window.innerWidth >= 1024) return;

    viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) / 2;
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      if (!trackFillRef.current) return;
      const travel = viewport.scrollWidth - viewport.clientWidth;
      const progress = travel > 0 ? viewport.scrollLeft / travel : 0;
      trackFillRef.current.style.width = `${progress * 100}%`;
    };
    update();
    viewport.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    setActiveCountryKey(OPENING_MARKER.country);
    setActiveMarkerKey(OPENING_MARKER_KEY);
    openingHint.current = true;
  }, []);

  // The design frames the strip centred, but the card hangs to the right of its
  // marker, so the opening one can land half outside. Nudge the strip along the
  // first time it is drawn — after that the visitor owns the scroll position.
  useEffect(() => {
    if (openingCardFramed.current) return;
    const viewport = viewportRef.current;
    const card = openingCardRef.current;
    if (!viewport || !card || window.innerWidth >= 1024) return;

    openingCardFramed.current = true;
    const overflow =
      card.offsetLeft +
      card.offsetWidth -
      (viewport.scrollLeft + viewport.clientWidth);
    if (overflow <= 0) return;

    viewport.scrollLeft = Math.min(
      viewport.scrollWidth - viewport.clientWidth,
      viewport.scrollLeft + overflow + 16,
    );
  }, [activeMarkerKey]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const getLocalTime = (timeZone: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone,
      }).format(now);
    } catch {
      return "—";
    }
  };

  const getUtcLabel = (timeZone: string) => {
    try {
      const offset = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        timeZoneName: "shortOffset",
      })
        .formatToParts(now)
        .find((part) => part.type === "timeZoneName")?.value;
      return offset === "GMT"
        ? "UTC+0"
        : (offset?.replace("GMT", "UTC") ?? "UTC");
    } catch {
      return "UTC";
    }
  };

  const endDrag = () => {
    dragState.current.active = false;
  };

  const scrubTo = (clientX: number) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const { left, width } = track.getBoundingClientRect();
    const reach = Math.min(1, Math.max(0, (clientX - left) / width));
    viewport.scrollLeft = reach * (viewport.scrollWidth - viewport.clientWidth);
  };

  return (
    <div className="flex w-[min(153.25rem,100%)] flex-col items-center gap-[5rem]">
      <p className="m-0 w-[min(34.25rem,100%)] text-center font-geist text-[2.25rem] leading-[1.5] text-white/60 lg:w-[min(100rem,100%)]">
        {statusPrefix}{" "}
        <strong className="font-semibold text-white">
          {activeCountry?.name.toUpperCase() ?? statusCountry}
        </strong>
      </p>

      <div className="flex h-[48.875rem] w-full shrink-0 flex-col items-center gap-[2.5625rem] lg:h-[57.0075rem] lg:gap-0">
        <div
          ref={viewportRef}
          onPointerDown={(event) => {
            if (window.innerWidth >= 1024) return;
            const viewport = viewportRef.current;
            if (!viewport) return;
            dragState.current = {
              active: true,
              startX: event.clientX,
              startScrollLeft: viewport.scrollLeft,
            };
            viewport.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const viewport = viewportRef.current;
            if (!viewport || !dragState.current.active) return;
            viewport.scrollLeft =
              dragState.current.startScrollLeft -
              (event.clientX - dragState.current.startX);
          }}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="h-[43.9375rem] w-full cursor-grab overflow-x-auto select-none [scrollbar-width:none] [touch-action:pan-y] active:cursor-grabbing [&::-webkit-scrollbar]:hidden lg:h-[57.0075rem] lg:cursor-default lg:overflow-visible">
          {/* The card's own footprint, used below to keep it inside the map on
              mobile — a horizontally scrollable box cannot show vertical
              overflow. Zeroed on desktop, where it is meant to hang past. */}
          <div
            className="relative isolate h-[43.9375rem] w-[118.1145rem] shrink-0 [--world-map-card-h:29.5rem] [--world-map-card-w:32rem] lg:h-[57.0075rem] lg:w-[153.25rem] lg:[--world-map-card-h:0rem] lg:[--world-map-card-w:0rem]"
            onMouseLeave={() => {
              openingHint.current = false;
              setActiveCountryKey(null);
              setActiveMarkerKey(null);
            }}
            onPointerDown={(event) => {
              if (event.pointerType === "mouse") return;
              openingHint.current = false;
              setActiveCountryKey(null);
              setActiveMarkerKey(null);
            }}>
            <Image
              src="/community/world-map/world-map.svg"
              alt=""
              fill
              sizes="(min-width: 1024px) 1226px, 945px"
              draggable={false}
              className={cn(
                "pointer-events-none z-0 object-fill transform-gpu transition-opacity duration-150 ease-out motion-reduce:transition-none",
                activeMarkerKey ? "opacity-70" : "opacity-100",
              )}
            />

            {COUNTRY_LAYERS.map((layer) => {
              const isActive = activeCountryKey === layer.country;
              const isInactive = Boolean(activeMarkerKey && !isActive);

              return (
                <div
                  key={layer.src}
                  className={cn(
                    "pointer-events-none absolute transition-opacity duration-150 ease-out motion-reduce:transition-none",
                    layer.className,
                    isActive ? "z-10 opacity-100" : "z-0",
                    isInactive && "opacity-40",
                  )}>
                  <Image
                    src={layer.src}
                    alt=""
                    fill
                    sizes="128px"
                    draggable={false}
                    className="object-fill"
                  />
                  <Image
                    src={layer.activeSrc}
                    alt=""
                    fill
                    sizes="128px"
                    draggable={false}
                    className={cn(
                      "object-fill transition-opacity duration-150 ease-out motion-reduce:transition-none",
                      isActive ? "opacity-100" : "opacity-0",
                    )}
                  />
                </div>
              );
            })}

            {CITY_MARKERS.map((marker, markerIndex) => {
              const markerKey = `${marker.country}-${marker.label}`;
              const country = countriesByKey.get(marker.country);
              if (!country) return null;

              const isActive = activeMarkerKey === markerKey;
              const isInactive = Boolean(activeMarkerKey && !isActive);

              return (
                <button
                  key={markerKey}
                  type="button"
                  aria-label={`Show ${country.name} — ${marker.label}`}
                  onPointerDown={(event) => {
                    lastPointerType.current = event.pointerType;
                    event.stopPropagation();
                    // Touch resolves on the press, not on the click: a click
                    // after a tap can be swallowed (scroll heuristics, iOS
                    // treating the first tap as a hover), and the visitor is
                    // left tapping twice. Mouse keeps using hover, below.
                    if (event.pointerType === "mouse") return;
                    const wasHint = openingHint.current;
                    openingHint.current = false;
                    if (isActive && !wasHint) {
                      setActiveCountryKey(null);
                      setActiveMarkerKey(null);
                      return;
                    }
                    setActiveCountryKey(marker.country);
                    setActiveMarkerKey(markerKey);
                  }}
                  onPointerEnter={(event) => {
                    if (event.pointerType !== "mouse") return;
                    openingHint.current = false;
                    setActiveCountryKey(marker.country);
                    setActiveMarkerKey(markerKey);
                  }}
                  onPointerLeave={(event) => {
                    if (event.pointerType !== "mouse") return;
                    setActiveCountryKey(null);
                    setActiveMarkerKey(null);
                  }}
                  onFocus={() => {
                    openingHint.current = false;
                    setActiveCountryKey(marker.country);
                    setActiveMarkerKey(markerKey);
                  }}
                  onBlur={() => {
                    setActiveCountryKey(null);
                    setActiveMarkerKey(null);
                  }}
                  onClick={() => {
                    // Touch already handled itself on pointerdown; this is the
                    // mouse path (and the keyboard one, which focus covers).
                    if (lastPointerType.current !== "mouse") return;
                    openingHint.current = false;
                    setActiveCountryKey(marker.country);
                    setActiveMarkerKey(markerKey);
                  }}
                  className={cn(
                    "absolute z-20 aspect-square w-[0.926%] cursor-pointer rounded-full border-0 bg-transparent p-0 transition-[transform,opacity] duration-150 ease-out motion-reduce:transition-none",
                    // Widens the ~8px dot for fingers. Capped at half the
                    // 11px gap in the Frankfurt/Berlin/Milan cluster.
                    "after:absolute after:-inset-[0.5rem] after:content-[''] lg:after:hidden",
                    isActive && "scale-[1.3] opacity-100",
                    !isActive && "scale-100",
                    isInactive && "opacity-35",
                  )}
                  style={{ left: marker.left, top: marker.top }}>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -inset-[0.25rem] rounded-full bg-[#98d8ff] opacity-25"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -inset-[0.625rem] animate-ping rounded-full bg-[#98d8ff] opacity-70 motion-reduce:hidden"
                    style={{
                      animationDuration: "3.6s",
                      animationDelay: `${markerIndex * 0.55}s`,
                    }}
                  />
                  <Image
                    src={marker.src}
                    alt=""
                    fill
                    sizes="12px"
                    draggable={false}
                  />
                </button>
              );
            })}

            <AnimatePresence>
              {activeCountry && activeCountryPosition && (
                <motion.div
                  key={activeCountryKey}
                  ref={
                    activeMarkerKey === OPENING_MARKER_KEY
                      ? openingCardRef
                      : undefined
                  }
                  className="pointer-events-none absolute z-30"
                  style={{
                    left: `min(${activeCountryPosition.left}, calc(100% - var(--world-map-card-w)))`,
                    top: `min(${activeCountryPosition.top}, calc(100% - var(--world-map-card-h)))`,
                  }}
                  initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: 4 }}
                  transition={{
                    duration: reduceMotion ? 0 : 0.18,
                    ease: [0.22, 1, 0.36, 1],
                  }}>
                  <WorldMapCountryCard
                    country={activeCountry}
                    localTime={getLocalTime(activeCountry.timeZone)}
                    utcLabel={getUtcLabel(activeCountry.timeZone)}
                    localTimeLabel={localTimeLabel}
                    onFloorLabel={onFloorLabel}
                    floorCapacity={floorCapacity}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex h-[2.375rem] w-full items-center gap-[2.75rem] lg:hidden">
          <div
            ref={trackRef}
            onPointerDown={(event) => {
              scrubbing.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
              scrubTo(event.clientX);
            }}
            onPointerMove={(event) => {
              if (scrubbing.current) scrubTo(event.clientX);
            }}
            onPointerUp={() => {
              scrubbing.current = false;
            }}
            onPointerCancel={() => {
              scrubbing.current = false;
            }}
            onLostPointerCapture={() => {
              scrubbing.current = false;
            }}
            className="relative h-full min-w-0 flex-1 cursor-pointer touch-none">
            <span className="absolute inset-x-0 top-1/2 h-[0.25rem] -translate-y-1/2 rounded-full bg-white/10" />
            <span
              ref={trackFillRef}
              className="absolute top-1/2 left-0 h-[0.25rem] -translate-y-1/2 rounded-full bg-white"
            />
          </div>
          <span className="shrink-0 font-geist text-[1.75rem] leading-[1.33] text-white/70">
            {dragLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
